import { env } from '@logspot-platform/env/embed';
import type { WidgetsHttpClient } from '@logspot-platform/widgets';

export const BASE_URL: string = env.VITE_APP_API_URL || '';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: 'Request failed' }));
    throw new ApiError(response.status, error.message || 'Request failed');
  }

  return response.json();
}

/**
 * OTT http client for the embedded dashboard: every request carries the
 * one-time-token in the `OTT-Authorization` header (no cookies/credentials).
 * Matches the legacy embed's API contract.
 */
export function createOttApi(token: string): WidgetsHttpClient {
  const buildHeaders = (extra?: Record<string, string>): HeadersInit => ({
    'Content-Type': 'application/json',
    'OTT-Authorization': `Bearer ${token}`,
    ...extra,
  });

  return {
    get: async <T>(
      path: string,
      options?: { headers?: Record<string, string>; signal?: AbortSignal },
    ): Promise<T> => {
      const response = await fetch(`${BASE_URL}${path}`, {
        method: 'GET',
        headers: buildHeaders(options?.headers),
        signal: options?.signal,
      });
      return handleResponse<T>(response);
    },

    post: async <T>(
      path: string,
      body?: unknown,
      options?: { headers?: Record<string, string>; signal?: AbortSignal },
    ): Promise<T> => {
      const response = await fetch(`${BASE_URL}${path}`, {
        method: 'POST',
        headers: buildHeaders(options?.headers),
        body: body ? JSON.stringify(body) : undefined,
        signal: options?.signal,
      });
      return handleResponse<T>(response);
    },
  };
}
